import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const BreakEvenPoint = () => {
  const [fixedCosts, setFixedCosts] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [variableCostPerUnit, setVariableCostPerUnit] = useState("");
  const [breakEvenPoint, setBreakEvenPoint] = useState(0.0);

  useEffect(() => {
    calculateBreakEven();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [fixedCosts, pricePerUnit, variableCostPerUnit]); // Runs whenever any input changes

  const calculateBreakEven = () => {
    const fixed = parseFloat(fixedCosts) || 0;
    const price = parseFloat(pricePerUnit) || 0;
    const variable = parseFloat(variableCostPerUnit) || 0;

    if (price > variable) {
      const breakEven = fixed / (price - variable);
      setBreakEvenPoint(breakEven.toFixed(2));
    } else {
      setBreakEvenPoint(0.0);
    }
  };

  return (
    <Layout title="Break-Even Point Calculator" path="break-even-point">
      <section className="break-even-calculator-section section-padding">
        <div className="container">
          <div className="row">
            {/* Break-Even Point Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Break-Even Point Calculator
                </h3>
                <div className="container">
                  {/* Input Fields for Break-Even Point Calculation */}
                  <div className="row gy-3">
                    {/* Fixed Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="fixedCosts" className="form-label">
                        Fixed Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Fixed Costs:</strong> The total fixed expenses incurred regardless of the production or sales volume."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="fixedCosts"
                        step="0.01"
                        placeholder="Enter fixed costs"
                        value={fixedCosts}
                        onChange={(e) => setFixedCosts(e.target.value)}
                      />
                    </div>
                    {/* Price per Unit */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="pricePerUnit" className="form-label">
                        Price per Unit (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Price per Unit:</strong> The selling price of one unit of the product or service."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="pricePerUnit"
                        step="0.01"
                        placeholder="Enter price per unit"
                        value={pricePerUnit}
                        onChange={(e) => setPricePerUnit(e.target.value)}
                      />
                    </div>
                    {/* Variable Cost per Unit */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="variableCostPerUnit"
                        className="form-label"
                      >
                        Variable Cost per Unit (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Variable Cost per Unit:</strong> The cost directly attributable to producing one unit of the product or service."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="variableCostPerUnit"
                        step="0.01"
                        placeholder="Enter variable cost per unit"
                        value={variableCostPerUnit}
                        onChange={(e) => setVariableCostPerUnit(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Break-Even Point (Units):{" "}
                        <span id="breakEvenPoint" className="fw-bold ms-3">
                          {breakEvenPoint}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Break-Even Point Calculator
                </h3>

                {/* What is a Break-Even Point Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Break-Even Point Calculator?
                </h4>
                <p>
                  A <strong>Break-Even Point (BEP) Calculator</strong> helps
                  businesses calculate the sales volume required to cover their
                  fixed and variable costs, allowing them to determine how many
                  units need to be sold to break even.
                </p>

                {/* Why Use a Break-Even Point Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Break-Even Point Calculator?
                </h4>
                <p>
                  Using a break-even point calculator offers several benefits:
                </p>
                <ul>
                  <li>
                    <strong>Sales Targeting:</strong> Helps set realistic sales
                    targets for your business.
                  </li>
                  <li>
                    <strong>Cost Management:</strong> Assists in understanding
                    financial structures and pricing strategies.
                  </li>
                  <li>
                    <strong>Profitability Assessment:</strong> Evaluates the
                    impact of sales volume on profitability.
                  </li>
                  <li>
                    <strong>Strategic Planning:</strong> Crucial for startups
                    and new product launches to know when they will become
                    profitable.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  Who Can Benefit from Using This Calculator?
                </h4>
                <p>
                  Entrepreneurs, small business owners, and startups can greatly
                  benefit from using this tool to make informed financial and
                  strategic decisions.
                </p>

                <h4 className="mt-4 mb-3">How Reliable Are the Results?</h4>
                <p>
                  The accuracy of the break-even analysis depends on the
                  precision of the input data. For best results, ensure that the
                  fixed and variable costs are current and accurately reflected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BreakEvenPoint;
